/**
 * Headless single/multi-select combobox primitive (T4 / FR-26).
 *
 * Why custom (not @headlessui/cmdk/radix-combobox): no combobox primitive is in
 * the existing dependency set, and the upload dialog only needs three shapes:
 *
 *   1. async search (debounced; caller supplies the search function),
 *   2. custom item renderer (name + aliases-as-secondary-text),
 *   3. single OR multi mode with the same surface.
 *
 * Keyboard contract: ArrowDown / ArrowUp navigate the open listbox, Enter
 * commits the highlighted option, Esc closes without changes, Tab closes and
 * keeps focus. The trigger is a plain text input with `role="combobox"` and
 * `aria-controls` pointing at the listbox, per WAI-ARIA APG 1.2 combobox spec.
 *
 * Nothing here is upload-specific — `UploadStepMetadata` and (potentially)
 * future pickers consume the same `<Combobox<T> />`.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";

export interface ComboboxOption<T> {
  /** Stable identifier; used for selection equality and React keys. */
  value: string;
  /** The underlying domain entity (Client/Employee/...); kept opaque here. */
  data: T;
  /** Primary label, shown bold in the list. */
  label: string;
  /** Optional secondary text — we use it for aliases in the upload form. */
  secondary?: string;
}

export interface ComboboxFooterAction {
  label: string;
  /** Called with the *current input value* so callers can prefill a form. */
  onSelect: (query: string) => void;
}

interface BaseProps<T> {
  /** Stable id for label-for/aria-labelledby wiring. */
  id?: string;
  /** Async search; result list is what the dropdown will render. */
  search: (query: string) => Promise<ComboboxOption<T>[]>;
  /** Debounce for `search`, in ms. Defaults to 300. */
  debounceMs?: number;
  placeholder?: string;
  disabled?: boolean;
  /** Optional footer action — e.g. "+ Create client" inline. */
  footerAction?: ComboboxFooterAction;
  /** Empty-state message when search returns no options. */
  emptyLabel?: string;
  /** `aria-invalid` passthrough for form error styling. */
  ariaInvalid?: boolean;
  className?: string;
}

export interface ComboboxSingleProps<T> extends BaseProps<T> {
  mode: "single";
  value: ComboboxOption<T> | null;
  onChange: (value: ComboboxOption<T> | null) => void;
}

export interface ComboboxMultiProps<T> extends BaseProps<T> {
  mode: "multi";
  value: ComboboxOption<T>[];
  onChange: (value: ComboboxOption<T>[]) => void;
  /** Render the chip row under the input. Defaults to built-in. */
  renderChips?: (
    selected: ComboboxOption<T>[],
    remove: (value: string) => void,
  ) => ReactNode;
  chipsLabel?: string;
}

export type ComboboxProps<T> = ComboboxSingleProps<T> | ComboboxMultiProps<T>;

/** Tiny inline debounce — no external dep needed for one consumer. */
function useDebouncedCallback<A extends unknown[]>(
  fn: (...args: A) => void,
  delay: number,
): (...args: A) => void {
  const ref = useRef(fn);
  ref.current = fn;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );
  return useCallback(
    (...args: A) => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => ref.current(...args), delay);
    },
    [delay],
  );
}

export function Combobox<T>(props: ComboboxProps<T>): ReactNode {
  const {
    id: idProp,
    search,
    debounceMs = 300,
    placeholder,
    disabled = false,
    footerAction,
    emptyLabel,
    ariaInvalid,
    className,
  } = props;
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const listboxId = `${id}-listbox`;

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ComboboxOption<T>[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const runSearch = useCallback(
    async (q: string) => {
      try {
        const result = await search(q);
        setOptions(result);
        setActiveIndex(result.length > 0 ? 0 : -1);
      } catch {
        setOptions([]);
        setActiveIndex(-1);
      }
    },
    [search],
  );
  const debouncedSearch = useDebouncedCallback(runSearch, debounceMs);

  // Open + initial load when the input gets focus.
  function handleFocus(): void {
    if (disabled) return;
    setOpen(true);
    void runSearch(query);
  }

  // Close on outside click.
  useEffect(() => {
    function onDocMouseDown(e: MouseEvent): void {
      if (!open) return;
      if (
        wrapRef.current !== null &&
        e.target instanceof Node &&
        !wrapRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  function commit(option: ComboboxOption<T>): void {
    if (props.mode === "single") {
      props.onChange(option);
      setQuery("");
      setOpen(false);
    } else {
      if (props.value.find((v) => v.value === option.value) === undefined) {
        props.onChange([...props.value, option]);
      }
      setQuery("");
      // For multi-select keep the menu open so the user can pick several.
      void runSearch("");
    }
  }

  function removeMulti(value: string): void {
    if (props.mode !== "multi") return;
    props.onChange(props.value.filter((v) => v.value !== value));
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        void runSearch(query);
        return;
      }
      setActiveIndex((i) =>
        options.length === 0 ? -1 : (i + 1) % options.length,
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) return;
      setActiveIndex((i) =>
        options.length === 0 ? -1 : (i - 1 + options.length) % options.length,
      );
      return;
    }
    if (event.key === "Enter") {
      if (!open) return;
      event.preventDefault();
      const opt = options[activeIndex];
      if (opt !== undefined) commit(opt);
      return;
    }
    if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        setOpen(false);
      }
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const next = e.target.value;
    setQuery(next);
    setOpen(true);
    debouncedSearch(next);
  }

  const inputValue = useMemo(() => {
    if (open) return query;
    if (props.mode === "single" && props.value !== null)
      return props.value.label;
    return query;
  }, [open, query, props]);

  const selected = props.mode === "multi" ? props.value : [];
  const renderChips =
    props.mode === "multi" ? (props.renderChips ?? defaultRenderChips) : null;

  return (
    <div className={cn("relative", className)} ref={wrapRef}>
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        type="text"
        autoComplete="off"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-invalid={ariaInvalid}
        aria-activedescendant={
          activeIndex >= 0 && options[activeIndex] !== undefined
            ? `${id}-opt-${options[activeIndex].value}`
            : undefined
        }
        placeholder={placeholder}
        disabled={disabled}
        value={inputValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      />

      {props.mode === "multi" && selected.length > 0 ? (
        <div
          className="mt-1 flex flex-wrap gap-1"
          aria-label={props.chipsLabel ?? "Selected"}
          data-testid={`${id}-chips`}
        >
          {renderChips !== null ? renderChips(selected, removeMulti) : null}
        </div>
      ) : null}

      {open ? (
        <ul
          id={listboxId}
          role="listbox"
          className={cn(
            "absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover py-1 text-sm shadow-md",
          )}
        >
          {options.length === 0 ? (
            <li
              className="px-3 py-1.5 text-muted-foreground"
              data-testid={`${id}-empty`}
            >
              {emptyLabel ?? "—"}
            </li>
          ) : null}
          {options.map((opt, idx) => (
            <li
              key={opt.value}
              id={`${id}-opt-${opt.value}`}
              role="option"
              aria-selected={idx === activeIndex}
              data-active={idx === activeIndex ? "true" : undefined}
              className={cn(
                "flex cursor-pointer flex-col px-3 py-1.5",
                idx === activeIndex ? "bg-accent text-accent-foreground" : null,
              )}
              onMouseEnter={() => setActiveIndex(idx)}
              // Use onMouseDown so the click commits *before* the input loses
              // focus and the dropdown auto-closes.
              onMouseDown={(e) => {
                e.preventDefault();
                commit(opt);
              }}
            >
              <span className="font-medium">{opt.label}</span>
              {opt.secondary !== undefined && opt.secondary.length > 0 ? (
                <span className="text-xs text-muted-foreground">
                  {opt.secondary}
                </span>
              ) : null}
            </li>
          ))}
          {footerAction !== undefined ? (
            <li
              role="option"
              aria-selected={false}
              data-testid={`${id}-footer-action`}
              className="mt-1 cursor-pointer border-t px-3 py-1.5 text-primary hover:bg-accent"
              onMouseDown={(e) => {
                e.preventDefault();
                footerAction.onSelect(query);
                setOpen(false);
              }}
            >
              {footerAction.label}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

function defaultRenderChips<T>(
  selected: ComboboxOption<T>[],
  remove: (value: string) => void,
): ReactNode {
  return selected.map((opt) => (
    <span
      key={opt.value}
      className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-xs"
      data-testid={`chip-${opt.value}`}
    >
      <span>{opt.label}</span>
      <button
        type="button"
        aria-label={`Удалить ${opt.label}`}
        className="ml-0.5 text-muted-foreground hover:text-foreground"
        onClick={() => remove(opt.value)}
      >
        ×
      </button>
    </span>
  ));
}
